import React from 'react'

import {
  AlignItems,
  BorderStyle,
  Box,
  Color,
  Display,
  Icon,
  IconVariant,
} from '@gousto-internal/citrus-react'
import { Alert } from 'goustouicomponents'
import PropTypes from 'prop-types'

import { PricePerPortionMessage } from 'components/PricePerPortionMessage'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useBoxes } from 'hooks/menu-boxes/useBoxes'
import { useMaxRecipesForPortion } from 'hooks/menu-boxes/useMaxRecipesForPortion'
import { useMinRecipesForPortion } from 'hooks/menu-boxes/useMinRecipesForPortion'
import { TooManyRecipesWarning } from 'routes/Menu/components/TooManyRecipesWarning/TooManyRecipesWarning'
import { useNumRecipesOverBasketLimit } from 'routes/Menu/domains/basket'

import { BoxProgressMessage } from '../../../BoxProgressMessage'

const BoxProgressAlert = ({ numRecipes, showPricePerPortionMessage = true }) => {
  const { isBoxesLoading } = useBoxes()
  const { numPortions } = useNumPortions()
  const maxRecipesNum = useMaxRecipesForPortion(numPortions)
  const minRecipesNum = useMinRecipesForPortion(numPortions)
  const hasMaxRecipes = numRecipes >= maxRecipesNum
  const hasMinRecipes = numRecipes >= minRecipesNum
  const numRecipesOverLimit = useNumRecipesOverBasketLimit()

  /* It doesn't make sense to show basket progress if we don't know what the maximum allowed number of recipe
  for the chosen portion size is, so we return null until we get that information from the menu-service. */
  if (isBoxesLoading) {
    return null
  }

  if (numRecipesOverLimit) {
    return (
      <Box paddingBottom={4}>
        <TooManyRecipesWarning numRecipesOverLimit={numRecipesOverLimit} />
      </Box>
    )
  }

  if (!showPricePerPortionMessage) {
    if (hasMinRecipes) {
      return null
    } else {
      return (
        <Box paddingBottom={10}>
          <Box
            data-testing="minimumRecipesBoxProgressAlert"
            display={Display.Flex}
            alignItems={AlignItems.Center}
            bg={Color.Informative_50}
            borderColor={Color.Informative_200}
            width="75"
            borderRadius={3}
            borderStyle={BorderStyle.Solid}
            borderWidth={1}
            paddingH={4}
            paddingV={4}
          >
            <Box display={Display.Flex} paddingRight={2}>
              <Icon name="info" size={5} variant={IconVariant.Informative} />
            </Box>
            <BoxProgressMessage
              numRecipes={numRecipes}
              showPricePerPortionMessaging={showPricePerPortionMessage}
            />
          </Box>
        </Box>
      )
    }
  }

  return (
    <div data-testing="boxProgressAlert">
      <Alert type={hasMaxRecipes ? 'success' : 'info'}>
        <PricePerPortionMessage />
        <BoxProgressMessage numRecipes={numRecipes} />
      </Alert>
    </div>
  )
}

BoxProgressAlert.propTypes = {
  numRecipes: PropTypes.number.isRequired,
  showPricePerPortionMessage: PropTypes.bool.isRequired,
}

export { BoxProgressAlert }
